// ============================================================
// THE PARTS OF A CONDITION - what every clause module shares.
//
// `effectWhen` is the language (judging, reading, saying); the clause rows
// live there and in `whenClauses`. Both take these types, so neither
// imports the other's values and there is no cycle.
//
// Pure. Zero React dependency.
// ============================================================

/** Somebody a clause may ask about: the target, or oneself. A missing part is not known - never "no". */
export interface Somebody {
  conditions?: readonly string[];
  wounds?: { current: number; max: number };
  /** Carries critical damage. */
  critical?: boolean;
  fatigue?: number;
  frenzied?: boolean;
  isHorde?: boolean;
  /** The scene says they have not noticed (Stealth's ladder), condition or not. */
  unaware?: boolean;
}

/** What is known when an effect is judged. A missing part is not known - never "no". */
export interface Situation {
  attack?: 'melee' | 'ranged';
  /** Who is attacked: null or absent when nobody is. */
  target?:
    | (Somebody & {
        size?: string;
        /** The origin classes (`originClassesOf`): XENOS, DAEMONICA... */
        origins?: readonly string[];
        tags?: readonly string[];
      })
    | null;
  self?: Somebody & {
    /** The names of the stances up. */
    stances?: readonly string[];
  };
  /** The attack's toggles as declared (aimHalf, aimFull, charge, calledShot, allOutAttack...). */
  action?: Readonly<Record<string, boolean>>;
  /** Where it happens, as the map (or the range control) says; judged after the map (plan armoury). */
  place?: {
    /** The band the attack rolls at: pointBlank, short, normal, long, extreme. */
    band?: string;
    /** The distance to the target, metres; null off the map. */
    metres?: number | null;
    /** The target's cover from the attacker: none, low, half, threeQuarter, full. */
    cover?: string;
  };
  /** Where one hit lands, and the worn armour there: known only per hit (the roll), never at declaration. */
  hit?: { location: string; armour: number };
  /** The armed weapon; absent with none armed. */
  weapon?: {
    /** Its special rules as written: "Tearing", "Blast(3)". */
    specials: readonly string[];
    damageType?: string;
    /** The firing mode chosen: single, semi or full. */
    mode?: string;
    /** Its free tags (plan armoury, stage 5). */
    tags?: readonly string[];
  };
}

export type Verdict = boolean | null;
export type Raw = Record<string, unknown>;

/** One kind of clause: how it is read from storage, judged and said. */
export interface ClauseRule<C extends { kind: string }> {
  /** The caption in the editor's list of kinds. */
  label: string;
  /** What a new clause of this kind starts as. */
  blank: C;
  /** The values on offer; absent for free text (a tag). */
  options?: readonly { id: string; label: string }[];
  /** The clause off storage, without `not`; null when this build cannot judge it. */
  read: (raw: Raw) => C | null;
  /** True, false, or null when the situation does not say. `not` is applied by the caller. */
  judge: (clause: C, s: Situation) => Verdict;
  /** The clause in words, positive and negated. */
  say: (clause: C) => [yes: string, no: string];
}

export const oneOf = <T extends string>(list: readonly T[], value: unknown): T | null =>
  list.includes(value as T) ? (value as T) : null;

/** A free-text value off storage, trimmed and kept short; '' when there is none. */
export const shortText = (value: unknown, max = 30): string =>
  typeof value === 'string' ? value.trim().slice(0, max) : '';
